import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Signpost as RoadIcon } from '@mui/icons-material';
import {
  deriveCameraOverlayLocations,
  fetchCameraOverlayLocationJob,
  formatCameraOverlayApiError,
  updateCameraOverlay,
  type CameraLocationJobDto,
  type CameraOverlayFileDto,
} from '../../services/cameraOverlayApi';
import { CAMERA_CATEGORY_LABELS } from '../../services/cameraOverlayService';

type Props = {
  file: CameraOverlayFileDto | null;
  onClose: () => void;
  /** Called after any successful change so the list can reload. */
  onChanged: () => void | Promise<void>;
};

const POLL_MS = 1500;

/**
 * Post-upload edits for a camera overlay: rename, file-wide source /
 * category (pushed onto every camera; clearing restores the per-camera
 * values), and "Derive road names" which labels each camera with the nearest
 * intersection / road so the map shows "Main St & 5th Ave" instead of an id.
 */
const CameraOverlayEditDialog: React.FC<Props> = ({ file, onClose, onChanged }) => {
  const [name, setName] = useState('');
  const [source, setSource] = useState('');
  const [category, setCategory] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [job, setJob] = useState<CameraLocationJobDto | null>(null);
  const [jobError, setJobError] = useState<string | null>(null);
  const fileId = file?.id ?? null;
  // Refs so the polling effect only re-subscribes when the file changes.
  const runningRef = useRef(false);
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;

  // Reset form whenever a different file opens.
  useEffect(() => {
    setName(file?.name ?? '');
    setSource(file?.source ?? '');
    setCategory(file?.category ?? '');
    setError(null);
    setJobError(null);
    setJob(null);
  }, [fileId, file?.name, file?.source, file?.category]);

  const applyJob = useCallback((next: CameraLocationJobDto) => {
    const wasRunning = runningRef.current;
    runningRef.current = next.status === 'running';
    setJob(next);
    // Refresh the list (location_count) when a run we were watching finishes.
    if (wasRunning && next.status !== 'running') void onChangedRef.current();
  }, []);

  // Read status on open, then poll every POLL_MS while a job is running.
  useEffect(() => {
    if (fileId == null) return undefined;
    let cancelled = false;
    const read = async () => {
      try {
        const next = await fetchCameraOverlayLocationJob(fileId);
        if (!cancelled) applyJob(next);
      } catch (e: unknown) {
        if (!cancelled) setJobError(formatCameraOverlayApiError(e, 'Could not read labeling status'));
      }
    };
    void read();
    const timer = window.setInterval(() => {
      if (runningRef.current) void read();
    }, POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [fileId, applyJob]);

  if (!file) return null;

  const dirty =
    name.trim() !== file.name || (source.trim() || null) !== (file.source ?? null) || (category || null) !== (file.category ?? null);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const patch: Parameters<typeof updateCameraOverlay>[1] = {};
      if (name.trim() !== file.name) patch.name = name.trim();
      if ((source.trim() || null) !== (file.source ?? null)) patch.source = source.trim() || null;
      if ((category || null) !== (file.category ?? null)) patch.category = category || null;
      await updateCameraOverlay(file.id, patch);
      await onChanged();
      onClose();
    } catch (e: unknown) {
      setError(formatCameraOverlayApiError(e, 'Failed to update overlay'));
    } finally {
      setSaving(false);
    }
  };

  const handleDerive = async (force: boolean) => {
    setJobError(null);
    try {
      const next = await deriveCameraOverlayLocations(file.id, force);
      applyJob(next);
    } catch (e: unknown) {
      setJobError(formatCameraOverlayApiError(e, 'Could not start road-name lookup'));
    }
  };

  const running = job?.status === 'running';
  const located = file.location_count ?? 0;
  const missing = Math.max(0, file.feature_count - located);
  const configured = job?.configured !== false;

  return (
    <Dialog open onClose={saving || running ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>Edit camera overlay</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Stack spacing={2}>
          <TextField
            label="Name"
            size="small"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            helperText="Shown in the Cameras filter list. Re-importing a file with this name replaces it."
          />
          <TextField
            label="Source"
            size="small"
            fullWidth
            value={source}
            onChange={(e) => setSource(e.target.value)}
            placeholder="e.g. City of Tucson DOT"
            helperText="Applied to every camera in this file. Leave blank to keep each camera's own source."
          />
          <FormControl size="small" fullWidth>
            <InputLabel id="camera-overlay-category-label">Category</InputLabel>
            <Select
              labelId="camera-overlay-category-label"
              label="Category"
              value={category}
              displayEmpty
              onChange={(e) => setCategory(String(e.target.value))}
              renderValue={(v) => (v ? CAMERA_CATEGORY_LABELS[String(v)] ?? String(v) : 'Mixed / inferred')}
            >
              <MenuItem value="">
                <em>Mixed / inferred (keep per-camera categories)</em>
              </MenuItem>
              {Object.entries(CAMERA_CATEGORY_LABELS).map(([slug, label]) => (
                <MenuItem key={slug} value={slug}>
                  {label}
                </MenuItem>
              ))}
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, mx: 1.75 }}>
              A category here is applied to every camera in the file; switching back to
              &ldquo;Mixed / inferred&rdquo; restores the categories detected at import.
            </Typography>
          </FormControl>
        </Stack>

        <Divider sx={{ my: 2.5 }} />

        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          <RoadIcon fontSize="small" color="action" />
          <Typography variant="subtitle2">Road names</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Community data rarely names cameras, so the map falls back to ids like
          <Box component="code" sx={{ mx: 0.5, fontSize: '0.8em' }}>node/14123982001</Box>.
          This looks up the named roads around each camera and labels it with the
          intersection (&ldquo;E Speedway Blvd &amp; N Campbell Ave&rdquo;) or nearest road. Labels
          are used on the map, in popups, the cameras-in-view strip and callouts; agency-supplied
          names are kept.
        </Typography>

        {!configured && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            Road lookup needs <code>MAPBOX_ACCESS_TOKEN</code> or a Google Maps key (<code>GOOGLE_MAPS_API_KEY</code> /{' '}
            <code>GOOGLE_API_KEY</code>) on the server.
          </Alert>
        )}
        {configured && job?.provider === 'google' && (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            Using Google Geocoding (the Street View key). Google returns the nearest road name;
            intersections (&ldquo;A &amp; B&rdquo;) need a Mapbox token on the server.
          </Alert>
        )}
        {jobError && (
          <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setJobError(null)}>
            {jobError}
          </Alert>
        )}
        {job?.status === 'error' && job.error && (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            Labeling stopped: {job.error}
            {(job.saved ?? 0) > 0 ? ` ${job.saved!.toLocaleString()} labels found before the stop were saved; run again to continue with the rest.` : ''}
          </Alert>
        )}

        <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
          <Typography variant="body2">
            <strong>{located.toLocaleString()}</strong> of {file.feature_count.toLocaleString()} cameras labelled
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            variant="contained"
            startIcon={<RoadIcon />}
            disabled={running || !configured || missing === 0}
            onClick={() => void handleDerive(false)}
          >
            {missing === 0 ? 'All labelled' : `Derive for ${missing.toLocaleString()} missing`}
          </Button>
          {located > 0 && (
            <Button size="small" variant="outlined" disabled={running || !configured} onClick={() => void handleDerive(true)}>
              Redo all
            </Button>
          )}
        </Stack>

        {running && job && (
          <Box sx={{ mt: 1.5 }}>
            <LinearProgress
              variant={job.total > 0 ? 'determinate' : 'indeterminate'}
              value={job.total > 0 ? Math.min(100, (job.done / job.total) * 100) : undefined}
            />
            <Typography variant="caption" color="text.secondary">
              {job.done.toLocaleString()} / {job.total.toLocaleString()} looked up · {job.labeled.toLocaleString()} labelled
              {job.failed > 0 ? ` · ${job.failed.toLocaleString()} failed` : ''}
              {(job.saved ?? 0) > 0 ? ` · ${job.saved!.toLocaleString()} saved` : ''}
            </Typography>
            {job.message && (
              <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
                {job.message}
              </Typography>
            )}
          </Box>
        )}
        {job?.status === 'done' && (
          <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 1 }}>
            Done: {job.labeled.toLocaleString()} labelled{job.failed > 0 ? `, ${job.failed.toLocaleString()} failed` : ''}
            {job.total > job.labeled + job.failed
              ? ` (${(job.total - job.labeled - job.failed).toLocaleString()} had no named road within 90 m)`
              : ''}
            {(job.rate_limited ?? 0) > 0 ? `; paused ${job.rate_limited!.toLocaleString()}× for provider rate limits` : ''}
            .{job.failed > 0 ? ' Run "Derive" again to retry the failed ones.' : ''}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Close
        </Button>
        <Button variant="contained" onClick={() => void handleSave()} disabled={!dirty || saving || !name.trim()}>
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CameraOverlayEditDialog;
